import React from "react";
import { Button } from "components/lib";

/**
 * Minimal React confirm dialog. Replaces window.confirm so the prompt is
 * styled, dismissable, and keyboard-friendly.
 *
 * Props:
 *   open        — boolean, controls visibility
 *   title       — heading
 *   body        — descriptive copy (string or React node)
 *   confirmText — primary button label (default "Confirm")
 *   cancelText  — secondary button label (default "Cancel")
 *   destructive — render the primary button in red (default false)
 *   onConfirm() — fired when the user confirms
 *   onCancel()  — fired when the user cancels (also bound to backdrop click)
 */
export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  body = null,
  confirmText = "Confirm",
  cancelText = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-2 text-lg font-semibold text-gray-900">{title}</h2>
        {body ? <div className="mb-4 text-sm text-gray-700">{body}</div> : null}
        <div className="flex justify-end gap-2">
          <Button small outline text={cancelText} action={onCancel} />
          <Button
            small
            color={destructive ? "red" : "blue"}
            text={confirmText}
            action={onConfirm}
          />
        </div>
      </div>
    </div>
  );
}
